import { CalendarDays, ClipboardCheck, Download, FileDown, FileText, MapPin, Printer, UserRoundCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { api, type ParticipantConference, type ParticipantConferenceSummary } from "../api.js";
import { apiErrorMessage } from "../../api/client.js";
import { Button } from "../../design/components/Button.js";
import { Alert, EmptyState, Skeleton } from "../../design/components/Feedback.js";
import { StatusBadge } from "../../design/components/StatusBadge.js";
import { formatDateRange } from "../formatters.js";

const tabLabels: Record<string, string> = {
  overview: "Overview",
  reviews: "Reviews",
  submissions: "Submissions",
  registration: "Registration",
  programme: "Programme",
  documents: "Documents",
};

const roleLabels: Record<string, string> = {
  REVIEWER: "Reviewer",
  AUTHOR: "Author",
  ATTENDEE: "Attendee",
  PRESENTER: "Presenter",
};

export function ParticipantConferenceIndex({ role }: { role?: ParticipantConferenceSummary["roles"][number] }) {
  const [conferences, setConferences] = useState<ParticipantConferenceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requestKey, setRequestKey] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    void api.participantConferences()
      .then((result) => { if (active) setConferences(result); })
      .catch((reason) => { if (active) setError(apiErrorMessage(reason, "Your conference work could not be loaded.")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [requestKey]);
  if (error) return <section className="participant-load-error">
    <div><p>My work</p><h1>We couldn't load your conference work</h1><span>Your access has not been changed. Try the request again.</span></div>
    <Alert tone="danger">{error}</Alert>
    <Button type="button" onClick={() => setRequestKey((current) => current + 1)}>Try again</Button>
  </section>;
  const visibleConferences = role
    ? conferences.filter((conference) => conference.roles.includes(role))
    : conferences;
  const heading = role === "ATTENDEE" ? "My registrations" : "My conference work";
  return <div className="participant-conference-index">
    <div className="participant-index-heading"><p>My work</p><h1>{heading}</h1><span>One account for every conference role. Open a conference to see only the work connected to you.</span></div>
    {loading ? <div className="participant-loading"><Skeleton /><Skeleton /></div> : visibleConferences.length ? <div className="participant-conference-cards">{visibleConferences.map((conference) => <article key={conference.id}>
      <Link className="participant-conference-card-main" to={`/my/conferences/${conference.id}/overview`}>
        <div><span>{conference.short_name || "Conference"}</span><h2>{conference.name}</h2><p>{formatDateRange(conference.starts_on, conference.ends_on)}</p></div>
        <strong>{conference.roles.map((role) => roleLabels[role] ?? role).join(" · ")}</strong>
      </Link>
      <Link className="participant-conference-next" to={conference.next_action.href}>{conference.next_action.label}</Link>
    </article>)}</div> : <EmptyState title={role === "ATTENDEE" ? "No registrations yet" : "No conference work yet"} description="Invitations, submissions, and registrations connected to your verified account will appear here." />}
  </div>;
}

function ReviewsPanel({ workspace }: { workspace: ParticipantConference }) {
  const reviews = workspace.reviews;
  if (!reviews) return <EmptyState title="No reviewer access" description="This conference is not connected to your account as a reviewer." />;
  if (!reviews.assignments.length) {
    return <section className="participant-waiting-state">
      <span><UserRoundCheck aria-hidden="true" size={22} /></span>
      <div>
        <p>Invitation accepted</p>
        <h2>You're ready to review</h2>
        <p>The organizer has not assigned a submission yet. When they do, it will appear here with its deadline and review form.</p>
      </div>
      {reviews.invitation ? <StatusBadge tone="success">Access ready</StatusBadge> : null}
    </section>;
  }
  return <div className="participant-conference-list">
    {reviews.assignments.map((assignment) => <Link key={assignment.id} to={`/my/reviews/${assignment.id}`}>
      <div><span>{assignment.reference || "Assigned submission"}</span><h2>{assignment.title}</h2></div>
      <StatusBadge tone={assignment.state === "COMPLETED" ? "success" : "info"}>{assignment.state === "COMPLETED" ? "Completed" : "Open review"}</StatusBadge>
    </Link>)}
  </div>;
}

function readableState(value: string) {
  return value.toLowerCase().replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase());
}

function RegistrationPanel({ workspace }: { workspace: ParticipantConference }) {
  const registration = workspace.registration;
  const [showPaymentReport, setShowPaymentReport] = useState(false);
  const [paymentReference, setPaymentReference] = useState("");
  const [reporting, setReporting] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState("");
  const [paymentError, setPaymentError] = useState("");
  if (!registration) return <EmptyState title="No registration" description="A registration connected to this verified account will appear here." />;
  const amount = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: registration.currency,
  }).format(registration.amount_minor / 100);
  const reportPayment = async () => {
    if (!paymentReference.trim()) return;
    setReporting(true); setPaymentError("");
    try {
      await api.reportRegistrationPayment(workspace.conference.id, workspace.conference.organization_id, registration.id, { amount_minor: registration.amount_minor, occurred_on: new Date().toISOString().slice(0, 10), method: "BANK_TRANSFER", reference: paymentReference.trim() }, `web-attendee-payment-${registration.id}-${crypto.randomUUID()}`);
      setPaymentNotice("Payment reported. It is waiting for organizer verification."); setShowPaymentReport(false);
    } catch (reason) { setPaymentError(apiErrorMessage(reason, "Your payment report could not be saved.")); }
    finally { setReporting(false); }
  };
  return <section className="participant-registration">
    <header><div><p>Attendee record</p><h2>Your registration</h2><span>{registration.reference}</span></div><StatusBadge tone="success">{readableState(registration.registration_state)}</StatusBadge></header>
    <dl>
      <div><dt>Category</dt><dd>{registration.category}</dd></div>
      <div><dt>Amount</dt><dd>{amount}</dd></div>
      <div><dt>Payment</dt><dd>{readableState(registration.payment_state)}</dd></div>
      <div><dt>Check-in</dt><dd>{readableState(registration.attendance_state)}</dd></div>
    </dl>
    <p>Only your attendee-facing status is shown here. Restricted answers and finance controls remain private.</p>
    {paymentError ? <Alert tone="danger">{paymentError}</Alert> : null}{paymentNotice ? <Alert tone="success">{paymentNotice}</Alert> : null}
    {registration.amount_minor > 0 && registration.payment_state === "AWAITING" && !showPaymentReport ? <Button type="button" variant="secondary" onClick={() => setShowPaymentReport(true)}>Report payment sent</Button> : null}
    {showPaymentReport ? <form className="participant-payment-report" onSubmit={(event) => { event.preventDefault(); void reportPayment(); }}><label>Payment reference<input value={paymentReference} onChange={(event) => setPaymentReference(event.target.value)} placeholder="Bank or transfer reference" required /></label><div><Button type="button" variant="ghost" onClick={() => setShowPaymentReport(false)}>Cancel</Button><Button type="submit" busy={reporting}>Submit payment report</Button></div><p>This records your report only. The organizer must verify the payment separately.</p></form> : null}
  </section>;
}

function ProgrammePanel({ workspace }: { workspace: ParticipantConference }) {
  const programme = workspace.programme;
  if (!programme?.sessions.length) {
    return <EmptyState title="Programme not published" description="The participant programme will appear here after the organizer publishes it." />;
  }
  return <div className="participant-programme">
    <div className="participant-programme-meta"><CalendarDays aria-hidden="true" size={18} /><span>Published programme · version {programme.version}</span><div className="programme-download-actions"><a href={`/api/v1/public/conferences/${workspace.conference.id}/programme/calendar/?organization_id=${workspace.conference.organization_id}`} download="programme.ics"><Download size={16} aria-hidden="true" />Download calendar</a><a href={`/api/v1/public/conferences/${workspace.conference.id}/programme.csv?organization_id=${workspace.conference.organization_id}`} download="programme.csv"><Download size={16} aria-hidden="true" />Download CSV</a><button type="button" onClick={() => window.print()}><Printer size={16} aria-hidden="true" />Print programme</button></div></div>
    {programme.sessions.map((session, sessionIndex) => <article key={`${session.starts_at}-${sessionIndex}`}>
      <header>
        <div><p>{session.day} · {session.date}</p><h2>{session.title}</h2><span>{session.room}{session.location ? ` · ${session.location}` : ""}</span></div>
        <time>{new Date(session.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</time>
      </header>
      {session.slots.length ? <div className="participant-programme-slots">{session.slots.map((slot, slotIndex) => <section key={`${slot.starts_at}-${slotIndex}`}>
        <div>{slot.reference ? <span>{slot.reference}</span> : null}<h3>{slot.title}</h3>{slot.presenters.length ? <p>{slot.presenters.join(" · ")}</p> : null}{slot.abstract ? <p className="participant-programme-abstract">{slot.abstract}</p> : null}</div>
        <time>{new Date(slot.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</time>
      </section>)}</div> : null}
    </article>)}
  </div>;
}

export function ParticipantConferenceWorkspace({ conferenceId, tab }: { conferenceId: string; tab: string }) {
  const [workspace, setWorkspace] = useState<ParticipantConference | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setError("");
    void api.participantConference(conferenceId)
      .then((result) => { if (active) setWorkspace(result); })
      .catch((reason) => { if (active) setError(apiErrorMessage(reason, "This conference workspace could not be opened.")); });
    return () => { active = false; };
  }, [conferenceId]);

  if (error) return <Alert tone="danger">{error}</Alert>;
  if (!workspace) return <div className="participant-loading"><Skeleton /><Skeleton /><Skeleton /></div>;
  const activeTab = workspace.tabs.includes(tab as ParticipantConference["tabs"][number]) ? tab : "overview";
  const conference = workspace.conference;

  return <div className="participant-conference">
    <Link className="participant-back" to="/my/conferences">← All conference work</Link>
    <header className="participant-conference-heading">
      <div>
        <p>My conference work</p>
        <h1>{conference.name}</h1>
        <div className="participant-conference-facts">
          {conference.starts_on ? <span><CalendarDays aria-hidden="true" size={15} />{formatDateRange(conference.starts_on, conference.ends_on)}</span> : null}
          {conference.venue ? <span><MapPin aria-hidden="true" size={15} />{conference.venue}</span> : null}
        </div>
      </div>
      <div className="participant-role-badges" aria-label="Your roles">{workspace.roles.map((role) => <StatusBadge key={role} tone="neutral">{roleLabels[role] ?? role}</StatusBadge>)}</div>
    </header>
    <nav className="participant-conference-tabs" aria-label="Conference work">
      {workspace.tabs.map((item) => <Link className={activeTab === item ? "is-active" : ""} key={item} to={`/my/conferences/${conferenceId}/${item}`}>{tabLabels[item]}</Link>)}
    </nav>
    <section className="participant-conference-content">
      {activeTab === "overview" ? <div className="participant-overview-grid">
        <article><ClipboardCheck aria-hidden="true" /><p>Your work</p><h2>{workspace.reviews?.counts.active ?? 0} reviews currently open</h2><span>Conferis shows only work attached to your verified account.</span></article>
        <article><FileText aria-hidden="true" /><p>Conference access</p><h2>{workspace.roles.map((role) => roleLabels[role]).join(" · ")}</h2><span>Each role remains separate and can be revoked independently.</span></article>
      </div> : null}
      {activeTab === "reviews" ? <ReviewsPanel workspace={workspace} /> : null}
      {activeTab === "submissions" ? workspace.submissions.length ? <div className="participant-conference-list">{workspace.submissions.map((submission) => <Link key={submission.id} to={`/my/submissions/${submission.id}`}><div><span>{submission.reference || "Draft"}</span><h2>{submission.title || "Untitled submission"}</h2></div><StatusBadge tone="info">{submission.state}</StatusBadge></Link>)}</div> : <EmptyState title="No submissions" description="Submissions linked to this account will appear here." /> : null}
      {activeTab === "registration" ? <RegistrationPanel workspace={workspace} /> : null}
      {activeTab === "programme" ? <ProgrammePanel workspace={workspace} /> : null}
      {activeTab === "documents" ? workspace.documents.length ? <div className="participant-documents">{workspace.documents.map((document) => <article key={document.id}><FileDown aria-hidden="true" /><div><h2>{document.name}</h2><p>{document.synthetic ? "Synthetic sample" : document.kind}</p></div><a href={document.preview_url} target="_blank" rel="noreferrer">Preview</a><a href={document.download_url} download>Download</a></article>)}</div> : <EmptyState title="No documents available" description="Authorized conference documents will appear here when they are released." /> : null}
    </section>
  </div>;
}
